import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import CategoryHero from "./CategoryHero";
import NewsTicker from "./NewsTicker";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { normalizeSocialUrl } from "./SocialLinks";
import { AdminItem, AdminAddBar } from "../admin/AdminControls";
import type { FieldDef } from "../admin/AdminFieldModal";

// Every caterer listing's public detail-page URL is derived automatically
// from its name: /catering-only/<name-slug>. Same pattern as templeSlug in
// Temples.tsx — no manual per-listing page creation needed.
export function catererSlug(name: string) {
  return (name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export interface Caterer {
  name: string;
  instagram: string;
  image: string;
  location?: string;
  contactNumber?: string;
  tagline?: string;
  address?: string;
  award?: string;
  type?: string | string[];
  facebook?: string;
}

const CATERER_FIELDS: FieldDef[] = [
  { key: "name", label: "Business name", required: true },
  { key: "image", label: "Image URL", type: "image", placeholder: "https://…" },
  { key: "tagline", label: "Tagline", placeholder: "Short description" },
  { key: "location", label: "Location", placeholder: "e.g. Plano" },
  { key: "address", label: "Address", placeholder: "Street, City, TX ZIP" },
  { key: "contactNumber", label: "Phone number", placeholder: "Digits only, e.g. 2145551234" },
  { key: "instagram", label: "Instagram URL", type: "url", placeholder: "https://www.instagram.com/…" },
  { key: "facebook", label: "Facebook URL", type: "url", placeholder: "https://www.facebook.com/…" },
  { key: "award", label: "Award / badge", placeholder: "e.g. DALLAS STAR CHEF 2026 Winner" },
  { key: "type", label: "Type", type: "select", options: ["Paan", "Home Delivery", "Halal"] },
];

const CATERING_TYPES = [
  { name: "Paan", image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783612202966-mhkaa7.png" },
  { name: "Home Delivery", image: "https://images.unsplash.com/photo-1526367790999-0150786686a2?w=200&h=200&fit=crop" },
  { name: "Halal", image: "https://images.unsplash.com/photo-1565557623262-b51c2513a641?w=200&h=200&fit=crop" },
];

export const CATERERS: Caterer[] = [
  {
    name: "Charcuterie Lab",
    instagram: "https://www.instagram.com/dallas.charcuterie.lab/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783613463837-vwxw.jpg",
    tagline: "Customized Charcuterie Boards, Boxes, Grazing Tables, Cups",
    location: "Dallas DFW",
    contactNumber: "2147628298",
  },
  {
    name: "5PM foods",
    instagram: "https://www.instagram.com/anuradha.sharma.2020/",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1782837094438-v1u6fn.jpg",
    tagline: "Indian Snacks, Desserts, Catering",
    address: "4021 Marigold Ln, Prosper, TX 75078",
    award: "DALLAS STAR CHEF 2026 Winner",
  },
  {
    name: "PAANCATERS",
    instagram: "https://www.instagram.com/paancaters?igsh=N2U5aW5hNHQ2MWIw&utm_source=qr",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-08/8d406249fef3.jpeg",
    location: "Plano",
    contactNumber: "5132904314",
    tagline: "Happiness is sweeter when shared",
    type: "Paan",
  },
  {
    name: "Mynaa Kitchen",
    instagram: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1783743404713-67b03.png",
    address: "6363 Dallas Pkwy Ste 110, Frisco, TX 75034",
  },
  {
    name: "Manna Kitchen",
    instagram: "",
    image: "https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=400&fit=crop",
    tagline: "Home made food delivered to your door step",
    facebook: "https://www.facebook.com/groups/1453476934919059/user/61582862256866",
    type: "Home Delivery",
  },
  {
    name: "SatAhaar Foods",
    instagram: "",
    image: "https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784421560289-j1hvlo.png",
    tagline: "100% Pure Vegetarian Gujarati & Punjabi meals",
    contactNumber: "6562048481",
  },
  {
    name: "Miracle Kitchen",
    instagram: "",
    image: "https://storage.googleapis.com/happyspace-generated-images/form-uploads/2026-07-22/6398ff36352c.jpeg",
    tagline: "North Indian Vegetarian Food Party Caterer",
    address: "McKinney, TX 75070",
    facebook: "https://www.facebook.com/share/1BfLJa21wR/?mibextid=wwXIfr",
    type: "Home Delivery",
  },
];

function caterMapsUrl(c: Caterer) {
  return (
    "https://www.google.com/maps/search/?api=1&query=" +
    encodeURIComponent(c.address || c.location || c.name)
  );
}

const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1555244162-803834f70033?w=600&h=400&fit=crop";

// Instagram post from TastebudsByAnubhi backing the Featured section on the
// Catering page — the featured card's badge (text left, gradient Instagram
// icon right) links to it. The section heading itself carries no icon.
const TASTEBUDS_FEATURED_LINK =
  "https://www.instagram.com/p/DeLB4UgMw1U/";

// Featured by TastebudsByAnubhi — currently Chaitanya's Delights (Little Elm).
// Address guard (same pattern as the Spice Rack check in Restaurants.tsx) so
// only the 2410 E Eldorado Pkwy card is featured, even if the name is edited.
export function isFeaturedCaterer(c: { name?: string; address?: string }) {
  const name = (c.name || "").trim().toLowerCase();
  const addr = (c.address || "").toLowerCase();
  return name.includes("chaitanya") && addr.includes("2410 e eldorado");
}

export function CatererCard({ caterer, index, isFeatured }: { caterer: Caterer; index: number; isFeatured?: boolean }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.08 }}
      className="bg-white rounded-md border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col max-w-[210px] mx-auto w-full"
      style={{ fontFamily: "'Poppins', sans-serif" }}
    >
      <Link to={`/catering-only/${catererSlug(caterer.name)}`} className="flex flex-col flex-1">
        <div className="w-full aspect-[4/3] relative overflow-hidden">
          <img
            src={caterer.image}
            alt={caterer.name}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.target as HTMLImageElement).src = FALLBACK_IMAGE;
            }}
          />
        </div>
        <div className="p-2 pb-0.5 flex-1">
          <h3 className="text-xs font-bold text-gray-900 leading-tight truncate">
            {caterer.name}
          </h3>
          {caterer.location && (
            <p className="text-[10px] text-gray-500 mt-0.5">{caterer.location}</p>
          )}
          {caterer.address && (
            <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-2">{caterer.address}</p>
          )}
          {caterer.tagline && (
            <p className="text-[10px] text-gray-400 italic mt-0.5 line-clamp-2">{caterer.tagline}</p>
          )}
          {caterer.award && (
            <p className="text-[9px] font-semibold text-[#f59e0b] mt-1 leading-tight">{caterer.award}</p>
          )}
          {isFeatured && (
            <a
              href={TASTEBUDS_FEATURED_LINK}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="mt-1 flex items-center gap-1 text-[9px] font-semibold text-green-600 tracking-wide hover:text-green-700 hover:underline"
              data-hs-id="caterer-featured-badge"
            >
              <span className="truncate">Featured by TastebudsByAnubhi</span>
              <svg
                className="w-3 h-3 shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="url(#tastebuds-ig-gradient)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <defs>
                  <linearGradient id="tastebuds-ig-gradient" x1="0" y1="1" x2="1" y2="0">
                    <stop offset="0%" stopColor="#f09433" />
                    <stop offset="45%" stopColor="#dc2743" />
                    <stop offset="100%" stopColor="#bc1888" />
                  </linearGradient>
                </defs>
                <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
              </svg>
            </a>
          )}
        </div>
      </Link>
      <div className="p-2 pt-1 flex flex-col gap-1">
        {caterer.contactNumber && (
          <a
            href={`tel:${caterer.contactNumber}`}
            className="w-full flex items-center justify-center gap-1 bg-[#51ba4f] hover:bg-[#459f43] text-white text-[10px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg> Call
          </a>
        )}
        {caterer.instagram ? (
          <a
            href={normalizeSocialUrl(caterer.instagram, "instagram") || caterer.instagram}
            target="_blank"
            rel="noopener noreferrer"
            style={{ background: "linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)" }}
            className="w-full flex items-center justify-center gap-1 hover:opacity-90 text-white text-[10px] font-semibold py-1 rounded-sm transition-opacity duration-200 cursor-pointer"
          >
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="20" x="2" y="2" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" x2="17.51" y1="6.5" y2="6.5"/></svg> Instagram
          </a>
        ) : caterer.facebook ? (
          <a
            href={normalizeSocialUrl(caterer.facebook, "facebook") || caterer.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-1 bg-[#1877F2] hover:bg-[#1364d4] text-white text-[10px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.5 9.87v-6.98H7.9V12h2.6V9.8c0-2.56 1.53-3.97 3.87-3.97 1.12 0 2.3.2 2.3.2v2.5h-1.3c-1.28 0-1.68.8-1.68 1.62V12h2.86l-.46 2.89h-2.4v6.98A10 10 0 0 0 22 12z"/></svg> Facebook
          </a>
        ) : caterer.address ? (
          <a
            href={caterMapsUrl(caterer)}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full flex items-center justify-center gap-1 bg-[#082f6d] hover:bg-[#0a3d8a] text-white text-[10px] font-semibold py-1 rounded-sm transition-colors duration-200 cursor-pointer"
          >
            <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" x2="21" y1="14" y2="3"/></svg> View Location
          </a>
        ) : caterer.contactNumber ? null : (
          <span className="w-full flex items-center justify-center gap-1 bg-gray-50 border border-gray-200 text-gray-400 text-[10px] font-semibold py-1 rounded-sm cursor-not-allowed">
            No Link
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default function Catering() {
  const [query, setQuery] = useState("");
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const mc = useManagedCollection<any>("catering", CATERERS, (c, i) => slugify(c.name || String(i)));

  const filtered = mc.items.filter(
    (d) =>
      (query.trim() === "" || d.name.toLowerCase().includes(query.trim().toLowerCase())) &&
      (!selectedType ||
        (Array.isArray(d.type) ? d.type.includes(selectedType) : d.type === selectedType))
  );

  const sortedFiltered = [...filtered].sort((a, b) => {
    const nameA = (a.name || "").trim().toLowerCase();
    const nameB = (b.name || "").trim().toLowerCase();

    const is5pmA = nameA === "5pm foods";
    const is5pmB = nameB === "5pm foods";

    if (is5pmA && !is5pmB) return -1;
    if (!is5pmA && is5pmB) return 1;

    return nameA.localeCompare(nameB);
  });

  // Featured by TastebudsByAnubhi section + "more" grid (same split as the
  // Restaurants page: featured cards first, everything else alphabetical).
  const featuredCaterers = sortedFiltered.filter((d) => isFeaturedCaterer(d));
  const moreCaterers = sortedFiltered.filter((d) => !isFeaturedCaterer(d));

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="Catering Services"
        placeholder="Search catering services…"
        backgroundImage="https://images.unsplash.com/photo-1555244162-803834f70033?w=1600&h=600&fit=crop"
        query={query}
        setQuery={setQuery}
      />
      <NewsTicker />

      {/* Explore by Type */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-4 pb-3">
          <p
            className="text-[11px] font-bold text-gray-400 uppercase mb-2"
            style={{ fontFamily: "'Poppins', sans-serif", letterSpacing: "0.12em" }}
          >
            Explore by Type
          </p>
          <div className="flex gap-6 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
            {CATERING_TYPES.map((t) => {
              const isActive = selectedType === t.name;
              return (
                <button
                  key={t.name}
                  onClick={() => setSelectedType(isActive ? null : t.name)}
                  className="flex flex-col items-center gap-2 shrink-0 group"
                >
                  <div
                    className={`w-[68px] h-[68px] rounded-full p-[2px] transition-transform duration-200 shadow-sm ${
                      isActive
                        ? "bg-gradient-to-tr from-[#51ba4f] to-[#3f9a3d] scale-110"
                        : "bg-gradient-to-tr from-[#082f6d] to-[#51ba4f] group-hover:scale-105"
                    }`}
                  >
                    <div className="w-full h-full rounded-full border-[2.5px] border-white overflow-hidden bg-white">
                      <img src={t.image} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                  </div>
                  <span
                    className={`text-[12px] font-medium transition-colors ${
                      isActive ? "text-[#51ba4f] font-semibold" : "text-gray-700"
                    }`}
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    {t.name}
                  </span>
                </button>
              );
            })}
            {selectedType && (
              <button
                onClick={() => setSelectedType(null)}
                className="flex items-center gap-1.5 rounded-full border border-dashed border-gray-300 text-gray-500 hover:text-[#51ba4f] hover:border-[#51ba4f] hover:bg-[#f0fdf4] whitespace-nowrap transition-colors duration-150 shrink-0 self-center"
                style={{ fontFamily: "'Poppins', sans-serif", height: "35px", padding: "0 13px", fontSize: "12.5px", fontWeight: 600 }}
              >
                ✕ Clear Filter
              </button>
            )}
          </div>
        </div>
      </div>

      <section className="bg-white border-b border-gray-100" style={{ fontFamily: "'Lora', Georgia, 'Times New Roman', serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 pt-4 pb-3">
          <h1 className="text-[16px] sm:text-[18px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Lora', Georgia, 'Times New Roman', serif" }}>
            Indian &amp; Desi Catering Services in Dallas–Fort Worth
          </h1>
          <p className="text-[13px] text-gray-500 leading-relaxed" style={{ fontFamily: "'Lora', Georgia, 'Times New Roman', serif" }}>
            Find Indian catering, Pakistani catering, Desi catering, and South Asian catering services across the Dallas–Fort Worth area. Discover local caterers offering vegetarian and non-vegetarian menus, biryani, curries, appetizers, sweets, desserts, buffet catering, and customized menus for weddings, birthdays, engagements, baby showers, housewarmings, corporate events, religious celebrations, and family gatherings.
          </p>
        </div>
      </section>

      <section className="bg-gray-50/40 py-5" style={{ fontFamily: "'Poppins', sans-serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 flex flex-col lg:flex-row gap-6">
          <div className="flex-1 min-w-0">
            <AdminAddBar mc={mc} fields={CATERER_FIELDS} label="Add Caterer" />
            {sortedFiltered.length > 0 ? (
              <>
                {/* Featured by TastebudsByAnubhi */}
                {featuredCaterers.length > 0 && (
                  <div className="mb-5">
                    <h3
                      className="text-[14px] sm:text-[16px] font-bold text-green-700 mb-3 flex items-center gap-2"
                      style={{ fontFamily: "'Lora', Georgia, serif" }}
                      data-hs-id="catering-featured-heading"
                    >
                      Featured by TastebudsByAnubhi
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {featuredCaterers.map((d, i) => (
                        <AdminItem key={d._key} mc={mc} item={d} fields={CATERER_FIELDS} title="Caterer">
                          <CatererCard caterer={d} index={i} isFeatured />
                        </AdminItem>
                      ))}
                    </div>
                  </div>
                )}

                {/* More Caterers */}
                {moreCaterers.length > 0 && (
                  <div>
                    {featuredCaterers.length > 0 && (
                      <h3 className="text-[14px] sm:text-[16px] font-bold text-gray-700 mb-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        More Caterers
                      </h3>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                      {moreCaterers.map((d, i) => (
                        <AdminItem key={d._key} mc={mc} item={d} fields={CATERER_FIELDS} title="Caterer">
                          <CatererCard caterer={d} index={i} />
                        </AdminItem>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white border border-dashed border-gray-200 rounded-2xl p-12 text-center"
              >
                <p className="text-[15px] text-gray-500 mb-2">Catering listings coming soon.</p>
                <p className="text-[12px] text-gray-400">
                  Know a great caterer?{" "}
                  <a href="/add-business" className="text-[#f97316] font-semibold hover:underline">
                    Add your business
                  </a>
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white border-t border-gray-100" style={{ fontFamily: "'Lora', Georgia, 'Times New Roman', serif" }}>
        <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-8">
          <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-[90ch]" style={{ fontFamily: "'Lora', Georgia, 'Times New Roman', serif" }}>
            Whether you're planning a small gathering or a large celebration, explore Indian caterers near you, Pakistani catering services, Desi food catering, Indian wedding catering, and South Asian catering in DFW. Find catering businesses serving Dallas, Frisco, Plano, Irving, McKinney, Allen, Carrollton, Richardson, Lewisville, Coppell, Prosper, and surrounding Dallas–Fort Worth communities.
          </p>
        </div>
      </section>
    </div>
  );
}
